import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  getEventOptions,
  getLocationOptions,
  getVendorOptions,
} from "../api/newReportingClient";

export interface ReportFilterValue {
  from: string; // yyyy-MM-dd
  to: string; // yyyy-MM-dd
  eventId: string; // "" = all events
  vendorId?: string; // "" / undefined = all vendors
  locationId?: string; // "" / undefined = all locations
  // Display names for the picked options ("" when "All …" is selected). Kept alongside the ids so
  // pages can label exports (e.g. the Vertical Sales PDF header) without re-fetching option lists.
  eventName?: string;
  vendorName?: string;
  locationName?: string;
}

interface NewReportFiltersProps {
  value: ReportFilterValue;
  onChange: (value: ReportFilterValue) => void;
  // Opt-in extra scope dropdowns (sourced from /vendors/options, /locations/options).
  includeVendor?: boolean;
  includeLocation?: boolean;
}

interface Opt {
  id: string;
  name: string;
}

// Loads a {id,name} option list once (when `load` is non-null), sorted by name.
function useOptions(load: (() => Promise<Opt[]>) | null): {
  options: Opt[];
  error: string | null;
} {
  const [options, setOptions] = useState<Opt[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!load) return;
    let cancelled = false;
    load()
      .then((opts) => {
        if (!cancelled) setOptions([...opts].sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load options");
      });
    return () => {
      cancelled = true;
    };
  }, [load]);
  return { options, error };
}

function OptionSelect({
  label,
  width,
  options,
  error,
  valueId,
  onPick,
}: {
  label: string;
  width: string;
  options: Opt[];
  error: string | null;
  valueId: string;
  onPick: (id: string, name: string) => void;
}) {
  const selectOptions = [
    { value: "", label: `All ${label.toLowerCase()}s` },
    ...options.map((o) => ({ value: o.id, label: o.name })),
  ];
  const selected = selectOptions.find((o) => o.value === valueId) ?? selectOptions[0];
  return (
    <label className={`flex ${width} flex-col text-sm font-medium text-gray-700`}>
      {label}
      <div className="mt-1">
        <Select
          options={selectOptions}
          value={selected}
          // The "All …" placeholder reports an empty name — only real picks carry a display name.
          onChange={(opt) => onPick(opt?.value ?? "", opt?.value ? opt.label : "")}
          isSearchable
          classNamePrefix="react-select"
          styles={{ menu: (base) => ({ ...base, zIndex: 20 }) }}
        />
      </div>
      {error && <span className="mt-1 text-xs font-normal text-red-600">{error}</span>}
    </label>
  );
}

// Self-contained filter bar for the new pages. Maps directly to the new API's query params:
// from / to / eventId (+ optional vendorId / locationId). Option lists come from the NEW system
// (/events|vendors|locations/options) because its GUIDs differ from the old Hasura integer ids.
export default function NewReportFilters({
  value,
  onChange,
  includeVendor,
  includeLocation,
}: NewReportFiltersProps) {
  const events = useOptions(getEventOptions);
  const vendors = useOptions(includeVendor ? getVendorOptions : null);
  const locations = useOptions(includeLocation ? getLocationOptions : null);

  return (
    <div className="flex flex-wrap items-end gap-4 py-3">
      <label className="flex flex-col text-sm font-medium text-gray-700">
        From
        <input
          type="date"
          value={value.from}
          max={value.to || undefined}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
          className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </label>

      <label className="flex flex-col text-sm font-medium text-gray-700">
        To
        <input
          type="date"
          value={value.to}
          min={value.from || undefined}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
          className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </label>

      <OptionSelect
        label="Event"
        width="w-72"
        options={events.options}
        error={events.error}
        valueId={value.eventId}
        onPick={(id, name) => onChange({ ...value, eventId: id, eventName: name })}
      />

      {includeVendor && (
        <OptionSelect
          label="Vendor"
          width="w-64"
          options={vendors.options}
          error={vendors.error}
          valueId={value.vendorId ?? ""}
          onPick={(id, name) => onChange({ ...value, vendorId: id, vendorName: name })}
        />
      )}

      {includeLocation && (
        <OptionSelect
          label="Location"
          width="w-64"
          options={locations.options}
          error={locations.error}
          valueId={value.locationId ?? ""}
          onPick={(id, name) => onChange({ ...value, locationId: id, locationName: name })}
        />
      )}
    </div>
  );
}
